import type { PdfPagePreview } from "@estada/plan-analyzer";

/** Keep vector previews where possible; render embedded bitmaps for scanned plans. */
export async function loadPdfImportPreview(bytes: Uint8Array, page: number, signal: AbortSignal): Promise<PdfPagePreview> {
  const library = await import("@estada/plan-analyzer");
  const preview = await library.getPdfPagePreview(bytes, { page, signal });
  if (!preview.warnings.some((warning) => warning.includes("Rasterbilder"))) return preview;
  const pdfjs = await import("pdfjs-dist");
  const worker = await import("pdfjs-dist/build/pdf.worker.mjs?url");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  if (signal.aborted) throw new DOMException("Abgebrochen", "AbortError");
  const task = pdfjs.getDocument({ data: bytes.slice() });
  const cancel = () => { void task.destroy(); };
  signal.addEventListener("abort", cancel, { once: true });
  try {
    const doc = await task.promise;
    const pdfPage = await doc.getPage(page);
    const original = pdfPage.getViewport({ scale: 1 });
    const scale = Math.min(2, 4096 / Math.max(original.width, original.height), Math.sqrt(8_000_000 / (original.width * original.height)));
    const viewport = pdfPage.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("PDF-Bildkontext konnte nicht erstellt werden.");
    await pdfPage.render({ canvasContext: context, viewport }).promise;
    if (signal.aborted) throw new DOMException("Abgebrochen", "AbortError");
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${original.width} ${original.height}" width="${original.width}" height="${original.height}"><g data-layer="original"><image width="${original.width}" height="${original.height}" href="${canvas.toDataURL("image/png")}"/></g></svg>`;
    return { ...preview, width: original.width, height: original.height, svg, warnings: [] };
  } finally {
    signal.removeEventListener("abort", cancel);
    await task.destroy();
  }
}
